import { Badge } from '@art-of-hospitality-solutions-llc/ui-kit/components/badge';
import { ColumnConfig } from '@art-of-hospitality-solutions-llc/ui-kit/components/data-table';
import { toastError } from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import { CellContext } from '@tanstack/react-table';
import { ResourceTableView } from '@Components/ResourceTable';
import { useResourceTableController } from '@Components/ResourceTable/useResourceTableController';
import { PaymentStatus, Purchase } from '@Types/purchase';
import { buildFilterParams } from '@Utils/buildFilterParams';
import { getPaymentTypes } from '@Utils/toast/purchaseHelpers';
import { getMemberProfile } from '@Utils/fish';
import { useGetPurchases } from '@Hooks/usePurchases';
import { Receipt } from 'lucide-react';
import { DateTime } from 'luxon';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AddressViewerDialog from './AddressViewerDialog';
import JsonViewerDialog from './JsonViewerDialog';

const formatCurrency = (value: number | null) => {
  if (value === null || value === undefined) return '-';
  return Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(value);
};

const purchaseColumnConfigs: ColumnConfig<Purchase>[] = [
  {
    id: 'confirmationNumber',
    headerLabel: 'Confirmation #',
    accessor: (row) => row.confirmationNumber ?? '-',
    type: 'text',
    size: 140,
  },
  {
    id: 'fName',
    headerLabel: 'First Name',
    accessor: (row) => getMemberProfile(row.fish)?.fName ?? '-',
    type: 'text',
    size: 120,
    enableSorting: false,
  },
  {
    id: 'lName',
    headerLabel: 'Last Name',
    accessor: (row) => getMemberProfile(row.fish)?.lName ?? '-',
    type: 'text',
    size: 120,
    enableSorting: false,
  },
  {
    id: 'productDescription',
    headerLabel: 'Product',
    accessor: (row) => row.productDescription,
    type: 'text',
    size: 200,
  },
  {
    id: 'status',
    headerLabel: 'Payment Status',
    accessor: (row) => row.status,
    type: 'text',
    size: 100,
    cell: (info) => {
      const status = info.getValue() as PaymentStatus;
      return <Badge variant="secondary">{status}</Badge>;
    },
  },
  {
    id: 'price',
    headerLabel: 'Amount',
    accessor: (row) => row.price,
    type: 'currency',
    size: 100,
    cell: (info) => formatCurrency(info.getValue() as number),
    meta: { align: 'right' as const },
  },
  {
    id: 'paymentType',
    headerLabel: 'Payment Type',
    accessor: (row) => getPaymentTypes(row.order?.checks),
    cell: (info: CellContext<Purchase, string[]>) => (
      <div className="flex flex-row gap-2">
        {info.getValue()?.map((type) => (
          <Badge key={`${info.cell.id}-payment-type-${type}`}>{type}</Badge>
        ))}
      </div>
    ),
    type: 'text',
    size: 120,
  },
  {
    id: 'createdAt',
    headerLabel: 'Created At',
    accessor: (row) =>
      row.createdAt ? DateTime.fromISO(row.createdAt).toMillis() : null,
    type: 'date',
    size: 180,
    cell: (info) => {
      const value = info.getValue() as number | null;
      return value
        ? DateTime.fromMillis(value).toLocaleString(DateTime.DATETIME_MED)
        : '-';
    },
  },
  {
    id: 'order',
    headerLabel: 'Order',
    accessor: (row) => row.order,
    type: 'text',
    size: 80,
    cell: (info) => (
      <JsonViewerDialog title="Order Details" data={info.row.original.order} />
    ),
    enableSorting: false,
  },
  {
    id: 'deliveryAddress',
    headerLabel: 'Delivery Address',
    accessor: (row) => row.deliveryAddress,
    type: 'text',
    size: 120,
    cell: (info) => (
      <AddressViewerDialog
        title="Delivery Address"
        address={info.row.original.deliveryAddress}
      />
    ),
    enableSorting: false,
  },
  {
    id: 'restaurantDetails',
    headerLabel: 'Restaurant',
    accessor: (row) => row.restaurantDetails?.general?.name ?? '-',
    type: 'text',
    size: 150,
    enableSorting: false,
  },
];

const PURCHASE_FIELD_MAPPING: Record<string, string> = {
  fName: 'filter[fish][fishProfiles][some][fName]',
  lName: 'filter[fish][fishProfiles][some][lName]',
};

const PURCHASE_STATIC_PARAMS: Record<string, string> = {
  'include[fish][include][fishProfiles][filter][deletedAt]': 'null',
};

export default function PurchaseList() {
  const navigate = useNavigate();

  const controller = useResourceTableController<Purchase>({
    columnsConfig: purchaseColumnConfigs,
    defaultSort: {
      id: 'createdAt',
      desc: true,
    },
    baseSearchParams: () => ({
      'include': 'fish.fishProfiles',
      'filter[deletedAt]': null,
    }),
    buildFilterParams: (filters) =>
      buildFilterParams(filters, {
        fieldMapping: PURCHASE_FIELD_MAPPING,
        staticParams: PURCHASE_STATIC_PARAMS,
      }),
    quickFilterFields: [
      { field: 'confirmationNumber', label: 'Confirmation #' },
      { field: 'productDescription', label: 'Product' },
    ],
    initialPageSize: 10,
  });

  const {
    data: purchases,
    status,
    isError,
    error,
  } = useGetPurchases(controller.searchParams);

  useEffect(() => {
    if (isError) {
      toastError(error.message);
    }
  }, [isError, error]);

  const rows: Purchase[] = Array.isArray(purchases)
    ? purchases
    : (purchases?.data ?? []);

  const meta = Array.isArray(purchases) ? undefined : purchases?.meta;

  return (
    <ResourceTableView<Purchase>
      label="Purchases"
      controller={controller}
      data={rows}
      meta={meta}
      status={status}
      summaryIcon={<Receipt className="h-4 w-4 text-zinc-500" />}
      summaryLabel="Total Purchases: "
      onRowClick={(row) => navigate(`/purchases/${row.id}`)}
    />
  );
}
